import React, { useState } from "react";
import { FileText, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import track from "@/lib/track";
import { toast } from "sonner";
import { TrustBadges } from "@/components/TrustBadges";
import { usePageMeta } from "@/components/SiteLayout";

const NEXT_STEPS = [
  { n: 1, t: "Intro call", d: "30 minutes to understand your asset and timeline" },
  { n: 2, t: "Confidential scoping", d: "Under NDA — we define exactly what we'll assess" },
  { n: 3, t: "Fixed proposal", d: "Scope, timeline and fees — no open-ended billing" },
];

export default function Contact() {
  usePageMeta(
    "Request a JCA Evidence Stress Test | JCA Matrix™",
    "A 2–4 week fixed-scope JCA Evidence Stress Test by MarksMan Healthcare. Know your evidence risks before assessors ask. Confidential, consultant-led."
  );
  return (
    <section className="pt-32 pb-24 md:pt-36 bg-slate-900 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <FileText className="h-9 w-9 text-blue-400" strokeWidth={1.5} />
          <h1 className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Know your JCA submission risks — before assessors ask.
          </h1>
          <p className="mt-5 text-slate-300 leading-relaxed">
            A 2–4 week fixed-scope JCA Evidence Stress Test delivered by MarksMan consultants using the
            JCA Matrix™ JCA Readiness & Strategy Platform. You receive:
          </p>
          <ul className="mt-6 space-y-2.5 text-slate-300 text-sm">
            {["Risk-focused Executive Report (20–30 pp)", "Executive dashboard & key findings", "PICO readiness assessment", "Top-10 strategic risk register", "Consultant-led risk read-out", "Executive board presentation"].map((t) => (
              <li key={t} className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0" /> {t}</li>
            ))}
          </ul>
          <div className="mt-8 rounded-xl border border-slate-700 bg-slate-800/50 p-5" data-testid="what-happens-next">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-400">What happens next</div>
            <div className="mt-3 space-y-3">
              {NEXT_STEPS.map((s) => (
                <div key={s.n} className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold shrink-0">{s.n}</span>
                  <div>
                    <span className="text-sm font-medium text-white">{s.t}</span>
                    <span className="text-xs text-slate-400"> — {s.d}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-500">Confidential. MarksMan performs the assessment end to end — nothing for your team to install or learn.</p>
          <div className="mt-8"><TrustBadges dark className="justify-start" /></div>
        </div>
        <ContactForm />
      </div>
    </section>
  );
}

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", asset_phase: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSending(true);
    try {
      await api.post("/inquiries", form);
      track("inquiry_submitted", "/contact");
      setSent(true);
    } catch { toast.error("Something went wrong — please try again."); }
    finally { setSending(false); }
  };

  if (sent) {
    return (
      <div data-testid="inquiry-success" className="rounded-2xl bg-white p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" strokeWidth={1.5} />
        <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">Request received</h3>
        <p className="mt-2 text-slate-600 text-sm">Our team will reach out within one business day to scope your engagement.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-8">
      <h3 className="font-display text-xl font-semibold tracking-tight">Request a JCA Evidence Stress Test</h3>
      <div className="mt-5 space-y-4">
        <Input data-testid="inquiry-name-input" placeholder="Full name *" value={form.name} onChange={set("name")} />
        <Input data-testid="inquiry-email-input" type="email" placeholder="Work email *" value={form.email} onChange={set("email")} />
        <Input data-testid="inquiry-company-input" placeholder="Company" value={form.company} onChange={set("company")} />
        <div className="grid grid-cols-2 gap-3">
          <Select value={form.role} onValueChange={setVal("role")}>
            <SelectTrigger data-testid="inquiry-role-select"><SelectValue placeholder="Your role" /></SelectTrigger>
            <SelectContent>
              {["HEOR", "Market Access", "Medical Affairs", "Clinical Development", "Biostatistics", "Executive Leadership", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.asset_phase} onValueChange={setVal("asset_phase")}>
            <SelectTrigger data-testid="inquiry-phase-select"><SelectValue placeholder="Asset phase" /></SelectTrigger>
            <SelectContent>
              {["Phase I", "Phase II", "Phase III", "Pre-submission", "During JCA", "Post-JCA"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Textarea data-testid="inquiry-message-input" placeholder="Tell us about your asset, indication and timeline…" rows={4} value={form.message} onChange={set("message")} />
        <Button data-testid="inquiry-submit-btn" onClick={submit} disabled={sending} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Send request
        </Button>
        <p className="flex items-start gap-1.5 text-[11px] text-slate-500 leading-snug" data-testid="inquiry-privacy-note">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-px" />
          Strictly confidential. Your information is used only to scope your engagement — never for AI training or marketing lists.
        </p>
      </div>
    </div>
  );
};
